import {ChangeDetectionStrategy, Component, DOCUMENT, EventEmitter, Inject, Input, OnInit, Output} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {CmsComponent, CmsService} from '@spartacus/core';
import {CmsComponentData} from '@spartacus/storefront';
import {Observable, of} from 'rxjs';
import {distinctUntilChanged, map, switchMap, take, tap} from 'rxjs/operators';

import {AimoSearchConfig} from '../../../../model/misc.model';
import {AimoProduct} from '../../../../model/product.model';
import {AimoGTMProductAttributes, GTMCartType} from '../../../../service/gtm/aimo-gtm.model';
import {AimoGtmService} from '../../../../service/gtm/aimo-gtm.service';
import {AimoProductService} from '../../../../service/product/aimo-product.service';
import {AimoGtmStoredParams} from "../../../../model/cart.model";
import {AimoCMSBannerComponent} from '../../../../model/cms.model';
import {AimoProductSearchService} from "../../../../service/product/search/aimo-product-search.service";
import {AimoAuthService} from "../../../../service/auth/aimo-auth.service";
import {AimoAssortmentService} from "../../../../service/product/aimo-assortment.service";

export const SEARCH_CONFIG: AimoSearchConfig = {currentPage: 0, pageSize: 19, sort: 'relevance'};

@Component({
    selector: 'aimo-cms-carousel',
    templateUrl: './aimo-cms-carousel.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AimoCMSCarouselComponent implements OnInit {
    @Input() data$: Observable<AimoCMSCarouselComponentData>;
    products$: Observable<Observable<AimoProduct>[]>;
    bannerComponent$: Observable<AimoCMSBannerComponent>;
    @Output()
    hasProducts = new EventEmitter<boolean>(undefined);

    gtmParams = {} as AimoGtmStoredParams;
    cartPage: boolean = false;
    @Input()
    showAsAccordion: boolean = false;

    constructor(
        protected componentData: CmsComponentData<AimoCMSCarouselComponentData>,
        protected productService: AimoProductService,
        protected activatedRoute: ActivatedRoute,
        protected gtmService: AimoGtmService,
        protected cmsService: CmsService,
        protected auth: AimoAuthService,
        protected productSearchService: AimoProductSearchService,
        protected aimoAssortmentService: AimoAssortmentService,
        @Inject(DOCUMENT) protected document: Document,
    ) {
    }

    ngOnInit(): void {
        this.data$ = this.data$ !== undefined ? this.data$ : this.componentData.data$;
        let idx = 0;
        this.products$ = this.data$.pipe(
            take(1),
            switchMap((component) => this.getProducts(component)),
            map((l) => l?.map((p) => of({...p, gtmProductAttributes: this.createGtmAttributes(idx++, p)}))),
        );
        this.cartPage = this.document.getElementById('cartDetailsWrapper') !== null;
    }

    getProducts(component: AimoCMSCarouselComponentData): Observable<AimoProduct[]> {
        this.showAsAccordion = component.showAsAccordion === true || component.showAsAccordion === 'true';
        this.bannerComponent$ = component.bannerComponent ? this.cmsService.getComponentData<AimoCMSBannerComponent>(component.bannerComponent.uid) : of({});
        return this.aimoAssortmentService.isOnlyMyAssortment().pipe(
            distinctUntilChanged((curr, prev) => curr === prev),
            switchMap(ownAssortment => {
                this.gtmParams = {...this.gtmParams};
                return this.productSearchService.getProducts(component, this.cartPage, this.gtmParams, this.activatedRoute, ownAssortment).pipe(
                    tap((products) => this.hasProducts.emit(products?.length > 0))
                )
            })
        );
    }

    createGtmAttributes(idx: number, product: AimoProduct): AimoGTMProductAttributes {
        return {
            ...this.gtmService.createProductGtmAttributesBase(product, undefined),
            index: idx,
            gtmParams: this.gtmParams,
            cart_type: GTMCartType.cart,
        } as AimoGTMProductAttributes;
    }
}

export interface AimoCMSCarouselComponentData extends CmsComponent {
    title?: string;
    carouselType?: CAROUSEL_TYPE;
    productCodes?: string;
    showAllText?: string;
    showAllUrl?: string;
    randomSort?: string;
    categoryCodes?: string;
    showAsAccordion?: string | boolean;
    visibleOnlyFor?: string;
    bannerComponent?: AimoCMSBannerComponent;
    tabIcon?: string;
}

export enum CAROUSEL_TYPE {
    PRODUCT_LIST = 'PRODUCT_LIST',
    LAST_PURCHASES = 'LAST_PURCHASES',
    NOVELTY = 'NOVELTY',
    CAMPAIGN = 'CAMPAIGN',
    SPONSORED = 'SPONSORED',
    SEASONAL = 'SEASONAL',
    FAVORITES = 'FAVORITES',
    REFERENCE = 'REFERENCE',
    SIMILAR_PRODUCTS = 'SIMILAR_PRODUCTS',
    CROSSSELING_PRODUCTS = 'CROSSSELING_PRODUCTS',
}
